import { useToasterContext } from '@/shared/context/ToasterContext.tsx';
import { useCallback } from 'react';
import {
    MAX_FILE_SIZE_IN_BYTES,
    UploadFileSource,
    useFulfillmentUploadContext,
} from '../context/FulfillmentUploadContext.tsx';
import { useTranslation } from 'react-i18next';
import { asyncDelay } from '@/shared/utils/async.ts';

export function useUploadFiles() {
    const { t } = useTranslation();
    const { addUploads } = useFulfillmentUploadContext();
    const { addToast } = useToasterContext();

    return useCallback(
        async (files: File[], source: UploadFileSource = 'local') => {
            if (!files.length) return;

            // check if files are too big
            const tooBigFiles = files.filter(file => file.size > MAX_FILE_SIZE_IN_BYTES);
            if (tooBigFiles.length) {
                addToast({
                    title: t('fulfillment.documentUpload.errors.fileSize', {
                        size: Math.round(MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)),
                    }),
                    type: 'error',
                });
                return;
            }

            // upload files
            const uploadedFiles = await addUploads(files, source);

            // wait for the next render cycle to ensure the uploaded file
            // is available in the documents list
            await asyncDelay(0);

            const successfulUploads = uploadedFiles.filter(upload => upload.status === 'uploaded');
            return successfulUploads;
        },
        [addToast, t, addUploads]
    );
}
